import React, { FormEvent, useCallback } from 'react';

import { WarningMessage } from '@eika/attention-message';
import { LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { IconTypes } from '@eika/icon';

import usePaymentLimits from '../../fetchHooks/usePaymentLimits';
import { Account, AccountNumber, EFaktura, EfakturaBatchPaymentRequest } from '../../generated-types';
import useShowPaymentResult from '../../hooks/useShowPaymentResult';
import useTranslation from '../../hooks/useTranslation';
import { payBatchEFaktura } from '../../services/batchedEfakturaService';
import SigningContextFlow from '../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../signingContext/signingContext';
import { eFakturaTrackingIDs } from '../../utils/gtm/trackIDs/eFaktura';
import ChoosePaymentAccountDropdown from './ChoosePaymentAccountDropdown';
import MultipleApprovalEFakturaSigningStatus from './MultipleApprovalEFakturaSigningStatus';
import MultipleEFakturaApprovalText from './MultipleEFakturaApprovalText';
import { UseSelectedDebitAccountType } from './hooks/useSelectedDebitAccount';
import validate from './util/validate';

type Props = {
    eFakturas: EFaktura[];
    formattedTotalAmount: string;
    debitAccountState: UseSelectedDebitAccountType;
    onClose: () => void;
    accounts: Account[];
    errorState: [string | undefined, React.Dispatch<string | undefined>];
};

const MultipleApprovalEFakturaModalContent: React.FC<Props> = ({
    eFakturas,
    formattedTotalAmount,
    debitAccountState,
    accounts,
    onClose,
    errorState: [error, setError],
}) => {
    const { cancel, approveMultipleEFaktura: approveMultipleEFakturaText } = useTranslation();
    const { data: paymentLimits } = usePaymentLimits();

    const [debitAccountNumber, , targetDebitAccount] = debitAccountState;

    const signAllEfaktura = useCallback(
        (fromAccountNumber: AccountNumber) => {
            const request: EfakturaBatchPaymentRequest = {
                efakturaIds: eFakturas.map((efaktura) => efaktura.id),
                fromAccountNumber,
            };
            return payBatchEFaktura(request);
        },
        [eFakturas],
    );

    const {
        startSigning,
        signingOperationPending,
        showSigningFlow,
        signingStatus,
        handleCancelSigningFlow,
        signingError,
    } = useSigningContext(signAllEfaktura);

    const signBatch = useCallback(() => {
        const errorMessage = validate(eFakturas, targetDebitAccount, paymentLimits, approveMultipleEFakturaText.error);
        setError(errorMessage);
        if (!debitAccountNumber || errorMessage) return;
        startSigning(debitAccountNumber);
    }, [
        setError,
        startSigning,
        debitAccountNumber,
        approveMultipleEFakturaText.error,
        eFakturas,
        targetDebitAccount,
        paymentLimits,
    ]);

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            signBatch();
        },
        [signBatch],
    );

    const showSigningResult = useShowPaymentResult();

    if (showSigningFlow) {
        return (
            <SigningContextFlow
                onClosePayment={handleCancelSigningFlow}
                title={approveMultipleEFakturaText.approveEFaktura}
            />
        );
    }

    if ((showSigningResult && signingStatus) || signingError) {
        return <MultipleApprovalEFakturaSigningStatus retrySigning={signBatch} close={onClose} />;
    }

    return (
        <section className="multi-approval-efaktura__modal">
            <div className="multi-approval-efaktura__modal-title-wrapper">
                <h2>{approveMultipleEFakturaText.doYouWantToApproveAll}</h2>
                <MultipleEFakturaApprovalText eFakturas={eFakturas} formattedTotalAmount={formattedTotalAmount} />
            </div>
            <form onSubmit={onSubmit}>
                <ChoosePaymentAccountDropdown debitAccountState={debitAccountState} items={accounts} error={error} />
                <WarningMessage
                    className="multi-approval-efaktura__modal-warning-message"
                    icon={IconTypes.UTROPSTEGN_24}
                    text={
                        <span className="multi-approval-efaktura__modal-warning-message-wrapper">
                            <span className="bold">
                                {approveMultipleEFakturaText.allEFakturasArePaidFromThisAccount}
                            </span>
                            <span>{approveMultipleEFakturaText.doYouWishToPayFromDifferentAccounts}</span>
                        </span>
                    }
                />
                <div className="multi-approval-efaktura__modal-submit-wrapper">
                    <ButtonWithSpinner
                        showSpinner={signingOperationPending}
                        type="submit"
                        data-track-id={eFakturaTrackingIDs.batchEFaktura.id}
                        data-track-linkname={approveMultipleEFakturaText.approveNow}
                    >
                        {approveMultipleEFakturaText.approveNow}
                    </ButtonWithSpinner>
                    <LinkButton onClick={onClose}>{cancel}</LinkButton>
                </div>
            </form>
        </section>
    );
};

export default MultipleApprovalEFakturaModalContent;
